import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { ButtonCta } from '@global-ecom/nitro-uds/elements';

import { useAuth } from 'hooks/useAuth';
import { useFeature } from 'hooks/useFeature';
import { useMiniCart } from 'hooks/useMiniCart';
import { usePdp } from 'hooks/usePdp';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { useTranslate } from 'hooks/useTranslations';
import { useWishlist } from 'hooks/useWishlist';
import { RemoveWishlistItemModal } from 'ui/components/RemoveWishlistItemModal';
import { SUPPORTED_COUNTRY } from 'utils/constants';

type IProductWishListButton = {
  disabled?: boolean;
  quantity?: number;
};

export const ProductWishlistButton: React.FC<IProductWishListButton> = ({
  disabled,
  quantity,
}) => {
  const [addingToWishlist, setAddingToWishlist] = useState(false);
  const [removingFromWishlist, setRemovingFromWishlist] = useState(false);

  const canRemoveWishlistItemsFromPDP = useFeature(
    'CAN_REMOVE_WISHLIST_ITEMS_FROM_PDP'
  );
  const auth = useAuth();
  const router = useRouter();
  const translate = useTranslate();
  const { localizeUrlPath, country } = useSiteConfig();

  // DO NOT SHOW miniCart for US & CA
  const showMiniCart =
    country !== SUPPORTED_COUNTRY.CA && country !== SUPPORTED_COUNTRY.US;

  const [wishlist, { add: addToWishlist, remove: removeFromWishList }] =
    useWishlist(false, showMiniCart);

  const { openMiniCart } = useMiniCart();
  const {
    desiredSize,
    desiredSwatch,
    masterProduct,
    productIsOrderable,
    setAddToCartError,
    isSimplifiedLayout,
  } = usePdp();

  const isMatchingItem = useCallback(
    ({ masterId, colorValue, sizeValue }) =>
      masterId === masterProduct?.id &&
      colorValue === desiredSwatch &&
      sizeValue === desiredSize,
    [masterProduct?.id, desiredSize, desiredSwatch]
  );

  const findWishlistItem = useCallback(() => {
    const wishListItems = wishlist.data?.wishList.items || [];

    return wishListItems.find(({ product }) => {
      const { masterId, colorValue, size } = product;

      return isMatchingItem({
        masterId,
        colorValue,
        sizeValue: size.value,
      });
    });
  }, [isMatchingItem, wishlist.data?.wishList.items]);

  // True if wishlist item
  const wishlistItem = useMemo(() => {
    const wishListItems = wishlist.data?.wishList.items || [];
    const isWishListEmpty = !wishListItems?.length;

    if (isWishListEmpty || !masterProduct) return undefined;

    return findWishlistItem();
  }, [wishlist, masterProduct, findWishlistItem]);

  const wishlistLoading = addingToWishlist || removingFromWishlist;
  const showRemoveWishListModal = removingFromWishlist && wishlistItem?.product;

  useEffect(() => {
    if (
      router.query.wishlist &&
      router.query.wishlist === 'true' &&
      masterProduct?.id &&
      desiredSize &&
      desiredSwatch &&
      productIsOrderable
    ) {
      if (wishlistItem || addingToWishlist) {
        return;
      }
      setAddingToWishlist(true);
      addToWishlist({
        input: {
          productId: masterProduct?.id,
          swatch: desiredSwatch as string,
          size: desiredSize as string,
          quantity,
        },
      })
        .then(res => {
          const item = res.data?.addItemToWishList.items.find(wishListItem => {
            isMatchingItem({
              masterId: wishListItem.product.masterId,
              colorValue: wishListItem.product.colorValue,
              sizeValue: wishListItem.product.size.value,
            });
          });

          if (item && showMiniCart) openMiniCart(item);
        })
        .catch(() => undefined)
        .finally(() => {
          const { wishlist, ...query } = router.query;
          setAddingToWishlist(false);
          router.replace({ query });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    desiredSize,
    desiredSwatch,
    masterProduct,
    productIsOrderable,
    router.query,
    isMatchingItem,
  ]);

  const addItemToWishlist = useCallback(async () => {
    setAddingToWishlist(true);
    await addToWishlist({
      input: {
        productId: masterProduct!.id,
        swatch: desiredSwatch as string,
        size: desiredSize as string,
        quantity,
      },
    }).then(res => {
      // Only for US & CA
      if (!showMiniCart)
        window.dispatchEvent(new Event('PRODUCT_WISHLIST_BUTTON_CLICKED'));

      const item = res.data?.addItemToWishList.items.find(wishListItem => {
        isMatchingItem({
          masterId: wishListItem.product.masterId,
          colorValue: wishListItem.product.colorValue,
          sizeValue: wishListItem.product.size.value,
        });
      });
      if (item && showMiniCart) openMiniCart(item);
    });

    setAddingToWishlist(false);
  }, [desiredSize, desiredSwatch, masterProduct, addToWishlist]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWishlistOnClick = useCallback(() => {
    if (!masterProduct) return;

    if (!desiredSize) {
      setAddToCartError(translate('pleaseSelectSize'));
      return;
    }

    // If guest, reroute to login page
    if (auth.guest) {
      router.push(
        localizeUrlPath(
          `account/login?from=pd/${router.query.model}/${router.query.id}&swatch=${router.query.swatch}&size=${router.query.size}&wishlist=true`
        )
      );
      return;
    }

    // If canRemoveWishlistItemsFromPDP, item can be added/removed from wishlist
    // Otherwise only add item.
    if (canRemoveWishlistItemsFromPDP) {
      if (!wishlistItem) {
        addItemToWishlist();
      } else {
        setRemovingFromWishlist(true); // triggers remove modal to show
      }
    } else if (wishlistItem && !showMiniCart) {
      removeWishlistItem();
    } else addItemToWishlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addItemToWishlist,
    auth.guest,
    canRemoveWishlistItemsFromPDP,
    desiredSize,
    localizeUrlPath,
    masterProduct,
    router.query,
    setAddToCartError,
    setRemovingFromWishlist,
    translate,
    wishlistItem,
  ]);

  const removeWishlistItem = useCallback(async () => {
    if (wishlistItem) {
      setRemovingFromWishlist(true);
      await removeFromWishList({
        input: { itemId: wishlistItem.id },
      });
      setRemovingFromWishlist(false);
    }
  }, [wishlistItem, removeFromWishList, setRemovingFromWishlist]);
  const wishlistButtonLabel = useMemo(
    () =>
      canRemoveWishlistItemsFromPDP
        ? translate(wishlistItem ? 'removeFromWishlist' : 'addToWishlist')
        : !showMiniCart && wishlistItem
        ? translate('miniWishListAddedToWishlist')
        : translate('addToWishlist'),
    [wishlistItem, translate, canRemoveWishlistItemsFromPDP, showMiniCart]
  );

  const switchHeartIcon = wishlistItem ? 'heart-filled' : 'heart';

  return (
    <>
      <ButtonCta
        id="add-to-wishlist"
        dataTestId="add-to-wishlist"
        disabled={disabled || wishlistLoading}
        label={wishlistButtonLabel}
        loading={auth.guest ? false : wishlistLoading}
        icon={switchHeartIcon}
        size="lg"
        variant={isSimplifiedLayout ? 'ghost' : 'secondary'}
        onClick={handleWishlistOnClick}
        {...(isSimplifiedLayout && { mode: 'icon' })}
        className={'p-0'}
      />
      {showRemoveWishListModal && wishlistItem?.product && (
        <RemoveWishlistItemModal
          onClickOutside={() => setRemovingFromWishlist(false)}
          onConfirm={removeWishlistItem}
          product={wishlistItem.product}
        />
      )}
    </>
  );
};
